/**
 * @octomil/browser — Telemetry reporter (v2 OTLP envelope)
 *
 * Opt-in, batched, non-blocking telemetry.  Events are queued in memory
 * and flushed periodically using `navigator.sendBeacon` (preferred) or
 * `fetch` with `keepalive: true`.
 *
 * V2 sends to `POST /api/v2/telemetry/events` with an OTLP-style resource
 * envelope wrapping each batch.
 */

import type { TelemetryEvent } from "./types.js";
import { OTLP_RESOURCE_ATTRIBUTES } from "./_generated/otlp_resource_attributes.js";
import { getInstallId } from "./install-id.js";
import type { BrowserRouteEvent } from "./route-event.js";
import { stripForbiddenKeys } from "./route-event.js";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_FLUSH_INTERVAL_MS = 30_000; // 30 seconds
const DEFAULT_MAX_BATCH_SIZE = 50;
const DEFAULT_TELEMETRY_URL = "https://api.octomil.com/api/v2/telemetry/events";
const SDK_NAME = "browser";
export const DEFAULT_SDK_VERSION = "1.0.0";

// ---------------------------------------------------------------------------
// Legacy resource envelope types (kept for backward compatibility)
// ---------------------------------------------------------------------------

/** OTLP-style resource descriptor included in every telemetry batch. */
export interface TelemetryResource {
  sdk: string;
  sdk_version: string;
  device_id: string;
  platform: string;
  org_id: string;
}

/** @deprecated Use ExportLogsServiceRequest instead. */
export interface TelemetryEnvelope {
  resource: TelemetryResource;
  events: TelemetryEvent[];
}

// ---------------------------------------------------------------------------
// OTLP/JSON types (OpenTelemetry Logs Data Model)
// ---------------------------------------------------------------------------

export interface OtlpKeyValue {
  key: string;
  value: {
    stringValue?: string;
    intValue?: string;
    doubleValue?: number;
    boolValue?: boolean;
  };
}

export interface OtlpResource {
  attributes: OtlpKeyValue[];
}

export interface OtlpInstrumentationScope {
  name: string;
  version?: string;
}

export interface OtlpLogRecord {
  timeUnixNano: string;
  severityNumber?: number;
  severityText?: string;
  body?: { stringValue: string };
  attributes?: OtlpKeyValue[];
  traceId?: string;
  spanId?: string;
}

export interface OtlpScopeLogs {
  scope: OtlpInstrumentationScope;
  logRecords: OtlpLogRecord[];
}

export interface OtlpResourceLogs {
  resource: OtlpResource;
  scopeLogs: OtlpScopeLogs[];
}

export interface ExportLogsServiceRequest {
  resourceLogs: OtlpResourceLogs[];
}

// ---------------------------------------------------------------------------
// TelemetryReporter
// ---------------------------------------------------------------------------

export interface TelemetryReporterOptions {
  /** Endpoint to POST batched events to. */
  url?: string;
  /** Flush interval in milliseconds. */
  flushIntervalMs?: number;
  /** Maximum events per batch. */
  maxBatchSize?: number;
  /** API key included in the `Authorization` header. */
  apiKey?: string;
  /** Optional auth header provider for device-token-authenticated browser clients. */
  authHeadersProvider?: () => Record<string, string> | null;
  /** Organisation identifier included in the resource envelope. */
  orgId?: string;
  /** Stable device identifier included in the resource envelope. */
  deviceId?: string;
  /** SDK version string. Defaults to the package version. */
  sdkVersion?: string;
}

export class TelemetryReporter {
  private readonly url: string;
  private readonly flushIntervalMs: number;
  private readonly maxBatchSize: number;
  private readonly apiKey: string | undefined;
  private readonly authHeadersProvider:
    | (() => Record<string, string> | null)
    | undefined;
  private readonly resource: TelemetryResource;

  private queue: TelemetryEvent[] = [];
  private timerId: ReturnType<typeof setInterval> | null = null;
  private disposed = false;

  constructor(options: TelemetryReporterOptions = {}) {
    this.url = options.url ?? DEFAULT_TELEMETRY_URL;
    this.flushIntervalMs =
      options.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL_MS;
    this.maxBatchSize = options.maxBatchSize ?? DEFAULT_MAX_BATCH_SIZE;
    this.apiKey = options.apiKey;
    this.authHeadersProvider = options.authHeadersProvider;
    this.resource = {
      sdk: SDK_NAME,
      sdk_version: options.sdkVersion ?? DEFAULT_SDK_VERSION,
      device_id: options.deviceId ?? generateDeviceId(),
      platform: SDK_NAME,
      org_id: options.orgId ?? "",
    };

    this.startAutoFlush();
  }

  // -----------------------------------------------------------------------
  // Public — resource updates
  // -----------------------------------------------------------------------

  /**
   * Merge additional fields into the telemetry resource.
   * Typically called with DeviceContext.telemetryResource() after
   * silent registration completes.
   */
  updateResource(fields: Record<string, string>): void {
    for (const [key, value] of Object.entries(fields)) {
      if (key === "device.id") this.resource.device_id = value;
      else if (key === "org.id") this.resource.org_id = value;
      else if (key === "platform") this.resource.platform = value;
    }
  }

  // -----------------------------------------------------------------------
  // Public — low-level
  // -----------------------------------------------------------------------

  /** Enqueue a telemetry event. Non-blocking, never throws. */
  track(event: TelemetryEvent): void {
    if (this.disposed) return;
    this.queue.push(event);

    if (this.queue.length >= this.maxBatchSize) {
      void this.flush();
    }
  }

  /** Flush all queued events immediately. */
  async flush(): Promise<void> {
    if (this.queue.length === 0) return;

    const batch = this.queue.splice(0, this.maxBatchSize);
    await this.send(batch);
  }

  async batch(events: TelemetryEvent[]): Promise<void> {
    if (events.length === 0) {
      return;
    }
    await this.send(events);
  }

  /** Stop the flush timer and send remaining events. */
  close(): void {
    if (this.disposed) return;
    this.disposed = true;

    if (this.timerId !== null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }

    // Best-effort final flush via beacon.
    if (this.queue.length > 0) {
      const remaining = this.queue.splice(0);
      const envelope = this.buildEnvelope(remaining);
      if (!this.sendBeaconPayload(envelope)) {
        void this.send(remaining);
      }
    }
  }

  // -----------------------------------------------------------------------
  // Public — named convenience methods (inference)
  // -----------------------------------------------------------------------

  reportInferenceStarted(
    modelId: string,
    attrs?: Record<string, string | number | boolean>,
  ): void {
    this.track(
      this.makeEvent("inference.started", {
        "model.id": modelId,
        ...attrs,
      }),
    );
  }

  reportInferenceCompleted(
    modelId: string,
    durationMs: number,
    attrs?: Record<string, string | number | boolean>,
  ): void {
    this.track(
      this.makeEvent("inference.completed", {
        "model.id": modelId,
        "inference.duration_ms": durationMs,
        ...attrs,
      }),
    );
  }

  reportInferenceFailed(
    modelId: string,
    errorType: string,
    errorMessage: string,
  ): void {
    this.track(
      this.makeEvent("inference.failed", {
        "model.id": modelId,
        "error.type": errorType,
        "error.message": errorMessage,
      }),
    );
  }

  reportInferenceChunk(
    modelId: string,
    attrs?: Record<string, string | number | boolean>,
  ): void {
    this.track(
      this.makeEvent("inference.chunk_produced", {
        "model.id": modelId,
        ...attrs,
      }),
    );
  }

  /**
   * Report that a single chunk was produced during streaming inference.
   *
   * Emits `inference.chunk_produced` with the model ID and chunk index.
   */
  reportChunkProduced(
    modelId: string,
    chunkIndex: number,
    attrs?: Record<string, string | number | boolean>,
  ): void {
    this.track(
      this.makeEvent("inference.chunk_produced", {
        "model.id": modelId,
        "inference.chunk_index": chunkIndex,
        ...attrs,
      }),
    );
  }

  // -----------------------------------------------------------------------
  // Public — named convenience methods (training)
  // -----------------------------------------------------------------------

  reportTrainingStarted(modelId: string, version: string): void {
    this.track(this.makeEvent("training.started", { modelId, version }));
  }

  reportTrainingCompleted(
    modelId: string,
    version: string,
    durationMs: number,
  ): void {
    this.track(
      this.makeEvent("training.completed", { modelId, version, durationMs }),
    );
  }

  reportTrainingFailed(
    modelId: string,
    version: string,
    errorType: string,
  ): void {
    this.track(
      this.makeEvent("training.failed", { modelId, version, errorType }),
    );
  }

  reportWeightUpload(
    modelId: string,
    roundId: string,
    sampleCount: number,
  ): void {
    this.track(
      this.makeEvent("training.weight_upload", {
        modelId,
        roundId,
        sampleCount,
      }),
    );
  }

  // -----------------------------------------------------------------------
  // Public — named convenience methods (deploy)
  // -----------------------------------------------------------------------

  reportDeployStarted(modelId: string, version: string): void {
    this.track(this.makeEvent("deploy.started", { modelId, version }));
  }

  reportDeployCompleted(
    modelId: string,
    version: string,
    durationMs: number,
  ): void {
    this.track(
      this.makeEvent("deploy.completed", { modelId, version, durationMs }),
    );
  }

  reportDeployRollback(
    modelId: string,
    fromVersion: string,
    toVersion: string,
    reason: string,
  ): void {
    this.track(
      this.makeEvent("deploy.rollback", {
        modelId,
        fromVersion,
        toVersion,
        reason,
      }),
    );
  }

  // -----------------------------------------------------------------------
  // Public — named convenience methods (experiment)
  // -----------------------------------------------------------------------

  reportExperimentAssigned(
    modelId: string,
    experimentId: string,
    variant: string,
  ): void {
    this.track(
      this.makeEvent("experiment.assigned", {
        modelId,
        experimentId,
        variant,
      }),
    );
  }

  reportExperimentMetric(
    experimentId: string,
    metricName: string,
    metricValue: number,
  ): void {
    this.track(
      this.makeEvent("experiment.metric", {
        experimentId,
        metricName,
        metricValue,
      }),
    );
  }

  // -----------------------------------------------------------------------
  // Public — route event reporting
  // -----------------------------------------------------------------------

  /**
   * Report a canonical route event. The event is privacy-sanitized before
   * being enqueued: any forbidden keys (prompt, output, audio, etc.) are
   * stripped at any nesting depth.
   *
   * This is the ONLY telemetry event uploaded for routing decisions.
   */
  reportRouteEvent(routeEvent: BrowserRouteEvent): void {
    const sanitized = stripForbiddenKeys(routeEvent);

    // Flatten the route event into OTLP-compatible flat attributes.
    const attrs: Record<string, string | number | boolean> = {
      "route.id": sanitized.route_id ?? "",
      "route.plan_id": sanitized.plan_id ?? "",
      "route.request_id": sanitized.request_id ?? "",
      "route.capability": sanitized.capability ?? "",
      "route.policy": sanitized.policy ?? "",
      "route.planner_source": sanitized.planner_source ?? "",
      "route.final_locality": sanitized.final_locality ?? "",
      "route.selected_locality": sanitized.selected_locality ?? "",
      "route.final_mode": sanitized.final_mode ?? "",
      "route.engine": sanitized.engine ?? "",
      "route.artifact_id": sanitized.artifact_id ?? "",
      "route.fallback_used": sanitized.fallback_used,
      "route.fallback_trigger_code": sanitized.fallback_trigger_code ?? "",
      "route.fallback_trigger_stage": sanitized.fallback_trigger_stage ?? "",
      "route.candidate_attempts": sanitized.candidate_attempts,
      "route.attempt_details": JSON.stringify(sanitized.attempt_details),
    };

    if (sanitized.app_id) attrs["route.app_id"] = sanitized.app_id;
    if (sanitized.app_slug) attrs["route.app_slug"] = sanitized.app_slug;
    if (sanitized.deployment_id)
      attrs["route.deployment_id"] = sanitized.deployment_id;
    if (sanitized.experiment_id)
      attrs["route.experiment_id"] = sanitized.experiment_id;
    if (sanitized.variant_id)
      attrs["route.variant_id"] = sanitized.variant_id;

    this.track(this.makeEvent("route.decision", attrs));
  }

  // -----------------------------------------------------------------------
  // Internal
  // -----------------------------------------------------------------------

  private makeEvent(
    name: string,
    attributes: Record<string, string | number | boolean>,
  ): TelemetryEvent {
    return {
      name,
      timestamp: new Date().toISOString(),
      traceId: generateHexId(16),
      spanId: generateHexId(8),
      attributes,
    };
  }

  private startAutoFlush(): void {
    if (typeof setInterval === "undefined") return;
    this.timerId = setInterval(() => {
      void this.flush();
    }, this.flushIntervalMs);
  }

  private buildEnvelope(events: TelemetryEvent[]): ExportLogsServiceRequest {
    const resourceAttrs = this.resourceToOtlpAttributes();
    const logRecords = events.map((e) => this.eventToLogRecord(e));

    return {
      resourceLogs: [
        {
          resource: { attributes: resourceAttrs },
          scopeLogs: [
            {
              scope: {
                name: `@octomil/${SDK_NAME}`,
                version: this.resource.sdk_version,
              },
              logRecords,
            },
          ],
        },
      ],
    };
  }

  private resourceToOtlpAttributes(): OtlpKeyValue[] {
    const attrs: OtlpKeyValue[] = [
      { key: "sdk", value: { stringValue: this.resource.sdk } },
      { key: "sdk_version", value: { stringValue: this.resource.sdk_version } },
      { key: "device_id", value: { stringValue: this.resource.device_id } },
      { key: "platform", value: { stringValue: this.resource.platform } },
      { key: "org_id", value: { stringValue: this.resource.org_id } },
    ];
    const installId = getInstallId();
    if (installId) {
      attrs.push({
        key: OTLP_RESOURCE_ATTRIBUTES.octomilInstallId,
        value: { stringValue: installId },
      });
    }
    return attrs;
  }

  private eventToLogRecord(event: TelemetryEvent): OtlpLogRecord {
    const attributes: OtlpKeyValue[] = Object.entries(event.attributes).map(
      ([key, val]) => ({
        key,
        value: this.toOtlpValue(val),
      }),
    );

    return {
      timeUnixNano: String(new Date(event.timestamp).getTime() * 1_000_000),
      severityNumber: 9, // INFO
      severityText: "INFO",
      body: { stringValue: event.name },
      attributes,
      traceId: event.traceId,
      spanId: event.spanId,
    };
  }

  private toOtlpValue(
    val: string | number | boolean,
  ): OtlpKeyValue["value"] {
    if (typeof val === "string") return { stringValue: val };
    if (typeof val === "boolean") return { boolValue: val };
    if (Number.isInteger(val)) return { intValue: String(val) };
    return { doubleValue: val as number };
  }

  private async send(events: TelemetryEvent[]): Promise<void> {
    const envelope = this.buildEnvelope(events);
    const body = JSON.stringify(envelope);
    const authHeaders = this.authHeadersProvider?.() ?? null;

    try {
      // Try sendBeacon first — it survives page unload.
      if (!authHeaders && this.sendBeaconPayload(envelope)) return;

      // Fallback to fetch with keepalive.
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (authHeaders) {
        Object.assign(headers, authHeaders);
      } else if (this.apiKey) {
        headers["Authorization"] = `Bearer ${this.apiKey}`;
      }

      await fetch(this.url, {
        method: "POST",
        headers,
        body,
        keepalive: true,
      });
    } catch {
      // Telemetry is best-effort. Swallow all errors.
    }
  }

  private sendBeaconPayload(envelope: ExportLogsServiceRequest): boolean {
    if (typeof navigator === "undefined" || !navigator.sendBeacon) {
      return false;
    }

    try {
      const blob = new Blob([JSON.stringify(envelope)], {
        type: "application/json",
      });
      return navigator.sendBeacon(this.url, blob);
    } catch {
      return false;
    }
  }
}

// ---------------------------------------------------------------------------
// ID generation helpers
// ---------------------------------------------------------------------------

/** Generate a hex string of `byteCount` random bytes (e.g. 16 → 32 hex chars). */
function generateHexId(byteCount: number): string {
  const bytes = new Uint8Array(byteCount);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    // Fallback for environments without crypto (test, SSR).
    for (let i = 0; i < byteCount; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Generate a stable-ish device ID using random hex. */
function generateDeviceId(): string {
  return `dev_${generateHexId(8)}`;
}

// ---------------------------------------------------------------------------
// Singleton helpers
// ---------------------------------------------------------------------------

let _reporter: TelemetryReporter | null = null;

export function initTelemetry(
  options: TelemetryReporterOptions = {},
): TelemetryReporter {
  if (_reporter) {
    _reporter.close();
  }
  _reporter = new TelemetryReporter(options);
  return _reporter;
}

export function getTelemetry(): TelemetryReporter | null {
  return _reporter;
}

export function closeTelemetry(): void {
  _reporter?.close();
  _reporter = null;
}
